import React from 'react'
import ReactDOM from 'react-dom/client'
import { ClerkProvider } from '@clerk/clerk-react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Toaster } from 'react-hot-toast'
import App from './App.tsx'
import { AppDialogProvider } from './components/common/AppDialogs'
import './index.css'

// Suppress development warnings in console for cleaner output
if (import.meta.env.DEV) {
  const originalWarn = console.warn;
  console.warn = (...args) => {
    const message = args[0];
    if (typeof message === 'string' && (
      message.includes('React Router Future Flag Warning') ||
      message.includes('React DevTools')
    )) {
      return; // Suppress these warnings
    }
    originalWarn.apply(console, args);
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
      cacheTime: 10 * 60 * 1000, // 10 minutes - data stays in cache
      refetchOnReconnect: false, // Don't refetch on reconnect
      refetchInterval: false, // Disable automatic refetching
    },
  },
})

// Determine if we're in development mode
const isDevelopment = import.meta.env.DEV;

const normalizeClerkPublishableKey = (key?: string) => {
  const trimmedKey = key?.trim()
  if (!trimmedKey) return trimmedKey

  // Clerk keys encode the instance host and usually end with "$" as "JA".
  // A common copy/paste mistake includes a trailing newline, producing "JAo".
  return /^pk_(test|live)_/.test(trimmedKey) && trimmedKey.endsWith('JAo')
    ? trimmedKey.slice(0, -1)
    : trimmedKey
}

// Use DEV Clerk key if in development and it exists, otherwise use production key
const rawClerkPublishableKey = (isDevelopment && (import.meta as any).env.VITE_CLERK_PUBLISHABLE_KEY_DEV) 
  ? (import.meta as any).env.VITE_CLERK_PUBLISHABLE_KEY_DEV 
  : (import.meta as any).env.VITE_CLERK_PUBLISHABLE_KEY;

const CLERK_PUBLISHABLE_KEY = normalizeClerkPublishableKey(rawClerkPublishableKey)



if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing Clerk Publishable Key')
}

// Add comprehensive error logging for Clerk
window.addEventListener('error', (event) => {
  if (event.error && event.error.message && event.error.message.includes('clerk')) {
    console.error('🚨 CLERK ERROR:', event.error);
    console.error('🚨 CLERK ERROR STACK:', event.error.stack);
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('clerk')) {
    console.error('🚨 CLERK PROMISE REJECTION:', event.reason);
  }
});

// Monitor Clerk token changes
setInterval(() => {
  const jwt = localStorage.getItem('clerk-db-jwt');
  // Only warn if it's not a dev session token (which starts with 'dvb_')
  if (jwt && jwt.length < 50 && !jwt.startsWith('dvb_')) {
    console.warn('🚨 MALFORMED JWT DETECTED:', jwt);
  }
}, 2000);


// Dynamic redirect URLs based on environment - let existing routing logic handle the rest
const baseUrl = isDevelopment ? window.location.origin : 'https://nexusaspyre.com';


// Preserve query parameters during Clerk redirects
const preserveQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params.toString() ? `?${params.toString()}` : '';
};

const signInFallbackRedirectUrl = `${baseUrl}/${preserveQueryParams()}`; // Redirect to root, let guards and HomepageRedirect handle routing
const signUpFallbackRedirectUrl = `${baseUrl}/${preserveQueryParams()}`; // Redirect to root, let guards and HomepageRedirect handle routing

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInFallbackRedirectUrl={signInFallbackRedirectUrl}
      signUpFallbackRedirectUrl={signUpFallbackRedirectUrl}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppDialogProvider>
            <App />
          </AppDialogProvider>
          <Toaster position="top-right" />
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  </React.StrictMode>,
)
